import { Button, DateRangeScrubber, Paper, Skeleton, Typography } from '@compass/components'
import { cn } from '@compass/utils'
import { CalendarIcon, ListFilterPlusIcon, Settings2Icon } from 'lucide-react'
import { DateTime } from 'luxon'

import { BOT_DETECTION_EDGE_SCRUBBER_RANGE_DAYS } from '../dateRanges'

const TABLE_COLUMNS = [
  { label: 'Date', className: 'min-w-37' },
  { label: 'Bot', className: 'min-w-46' },
  { label: 'Identity', className: 'min-w-32' },
  { label: 'Category', className: 'min-w-56 max-w-56' },
  { label: 'URL', className: 'w-full max-w-[32ch]' },
  { label: 'IP Address', className: 'min-w-40' },
  { label: 'Event ID', className: 'min-w-50' },
] as const
const SKELETON_ROW_COUNT = 8

// A fully static, non-interactive replica of the Events page used as a backdrop behind
// `BotDetectionUpsellModal` for workspaces without Bot Detection.
export function BotDetectionEventsSkeleton() {
  const now = DateTime.now()
  const startOfToday = now.startOf('day')
  const scrubberDomainStart = startOfToday.minus({ days: BOT_DETECTION_EDGE_SCRUBBER_RANGE_DAYS })
  const scrubberDomainEnd = now.endOf('day')

  return (
    <div className='flex flex-col gap-4'>
      <div className='-mx-4 pointer-events-none md:-mx-6' aria-hidden>
        <DateRangeScrubber
          startDate={scrubberDomainStart}
          endDate={scrubberDomainEnd}
          selectedStartDate={startOfToday}
          selectedEndDate={scrubberDomainEnd}
          selectedRangeLabel='Today'
          buckets={[]}
          granularity='day'
          rangeContainerClassName='mx-4 md:mx-6'
          axisClassName='-left-4 -right-4 md:-left-6 md:-right-6'
        />
      </div>
      <div className='flex flex-col gap-3 sm:flex-row'>
        <Button variant='secondary' isDisabled>
          <ListFilterPlusIcon />
          Filters
        </Button>
        <Button variant='secondary' isDisabled>
          <CalendarIcon />
          Today
        </Button>
      </div>
      <Paper className='overflow-hidden p-0' padded={false}>
        <div className='flex items-center justify-between gap-2 px-4 py-3 border-b'>
          <Skeleton className='w-40 h-4' />
          <Button variant='ghost' isDisabled>
            <Settings2Icon />
            Display
          </Button>
        </div>
        <table className='w-full'>
          <thead>
            <tr className='bg-gray-100 border-b'>
              {TABLE_COLUMNS.map((column) => (
                <th key={column.label} className={cn('px-4 py-3 text-left', column.className)}>
                  <Typography as='div' size='xs' weight='medium' transform='uppercase' className='text-gray-800'>
                    {column.label}
                  </Typography>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: SKELETON_ROW_COUNT }, (_, rowIndex) => (
              <tr key={rowIndex} className='group'>
                {TABLE_COLUMNS.map((column) => (
                  <td
                    key={column.label}
                    className={cn('h-11.25 px-4 border-b group-last:border-b-0', column.className)}
                  >
                    <Skeleton className={cn('h-4', rowIndex % 2 === 0 ? 'w-3/4' : 'w-1/2')} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Paper>
    </div>
  )
}
